import { Controller } from "@hotwired/stimulus"

// How long to wait for GenerateChsNegotiationNoteJob's broadcast before letting the
// operator write the note themselves. Turbo Stream broadcasts are ephemeral: a job
// that finishes before this drawer's subscription is established (or a dropped
// broadcast, or a backed-up queue) would otherwise leave the drawer stuck on an
// empty readonly textarea with a disabled submit button.
const GENERATION_TIMEOUT_MS = 20000

/*
Keeps the Submit-to-CHS drawer's submit button in sync with the note textarea, so a
negotiation can never be submitted without a note, and guarantees the drawer stays
usable even if the drafted note never arrives.

The controller lives on the note-field wrapper, which the job replaces over a Turbo
stream: Stimulus reconnects on the replacement, so the button is enabled as soon as a
drafted (or fallback) note lands, and it also follows anything the operator types or
deletes. Once the operator has typed their own note, a late broadcast for this field is
discarded rather than overwriting their work.
*/
export default class extends Controller {
  static values = { submitId: String, generating: Boolean, fallbackNote: String }
  static targets = ["textarea", "indicator", "timeoutHint"]

  connect() {
    this.userEdited = false
    this.boundBeforeStreamRender = this.beforeStreamRender.bind(this)
    document.addEventListener("turbo:before-stream-render", this.boundBeforeStreamRender)

    if (this.generatingValue) {
      this.timeout = setTimeout(() => this.stopWaiting(), GENERATION_TIMEOUT_MS)
    }

    this.toggle()
  }

  disconnect() {
    clearTimeout(this.timeout)
    document.removeEventListener("turbo:before-stream-render", this.boundBeforeStreamRender)
  }

  // data-action="input->chs-negotiation-note#noteChanged" — this also fires for the
  // deletions that should re-disable the button.
  noteChanged() {
    this.userEdited = true
    this.toggle()
  }

  toggle() {
    const container = document.getElementById(this.submitIdValue)
    const button = container && container.querySelector("[type=submit]")
    if (!button) return
    // A toggle scheduled on a wrapper that has since been replaced must not disable the
    // button over a note it can no longer see.
    if (!this.hasTextareaTarget) return

    button.disabled = this.textareaTarget.value.trim() === ""
  }

  // Give up on the broadcast: unlock the field so the operator can write the note by
  // hand rather than being stuck behind a generation they cannot see the result of.
  // The deterministic note operators used to always get pre-filled is seeded here, so
  // a lost broadcast never leaves them with less than they had before the drawer
  // drafted anything.
  stopWaiting() {
    if (!this.hasTextareaTarget) return

    this.textareaTarget.readOnly = false
    this.textareaTarget.placeholder = ""
    this.textareaTarget.classList.remove("bg-support-light-200")

    if (this.textareaTarget.value.trim() === "") {
      this.textareaTarget.value = this.fallbackNoteValue
    }

    if (this.hasIndicatorTarget) this.indicatorTarget.classList.add("hidden")
    if (this.hasTimeoutHintTarget) this.timeoutHintTarget.classList.remove("hidden")

    this.toggle()
  }

  beforeStreamRender(event) {
    const streamTarget = event.target.getAttribute("target")

    // The submit button is replaced in an enabled state, so re-evaluate it once the
    // replacement has rendered: it must not end up enabled over a note the operator
    // has since cleared, or over a note replacement that was canceled below.
    if (streamTarget === this.submitIdValue) {
      setTimeout(() => this.toggle())
      return
    }

    if (streamTarget !== this.element.id) return

    // The draft did arrive, so stop counting down to the "taking longer than expected"
    // state even if the replacement itself is canceled below.
    clearTimeout(this.timeout)

    // A drafted note that lands after the operator started writing must not wipe out
    // what they typed, so cancel the replacement of this field (the submit-button
    // replacement, which targets a different element, still applies).
    if (this.userEdited) event.preventDefault()
  }
}
